import { useId, useState, type ReactNode } from 'react';

type AccordionProps = {
  title: ReactNode;
  summary?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

function readOpen(key: string | undefined, fallback: boolean) {
  if (!key) return fallback;
  try {
    const saved = localStorage.getItem(`mc-panel-accordion:${key}`);
    return saved === 'true' ? true : saved === 'false' ? false : fallback;
  } catch {
    console.warn('開閉状態を読み込めないため、既定の表示を使います。');
    return fallback;
  }
}

export function Accordion({
  title,
  summary,
  children,
  defaultOpen = false,
  storageKey,
  headingLevel = 3,
  className = '',
}: AccordionProps) {
  const id = useId();
  const [open, setOpen] = useState(() => readOpen(storageKey, defaultOpen));
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  function toggle() {
    const next = !open;
    setOpen(next);
    if (!storageKey) return;
    try {
      localStorage.setItem(`mc-panel-accordion:${storageKey}`, String(next));
    } catch {
      console.warn('開閉状態を保存できません。この画面では引き続き開閉できます。');
    }
  }
  return (
    <section className={`card accordion ${className}`}>
      <Heading className="accordion-heading">
        <button
          type="button"
          className="accordion-toggle"
          id={`${id}-heading`}
          aria-expanded={open}
          aria-controls={`${id}-content`}
          onClick={toggle}
        >
          <span className="accordion-title">{title}</span>{' '}
          {summary && <span className="accordion-summary small muted">{summary}</span>}
          <span className="accordion-marker" aria-hidden="true">
            {open ? '▾' : '▸'}
          </span>
        </button>
      </Heading>
      <div id={`${id}-content`} aria-labelledby={`${id}-heading`} hidden={!open} className="card-body stack">
        {children}
      </div>
    </section>
  );
}
